import { FC } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { FilterIcon, SquareArrowOutUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { useSearchPage } from "./SearchPage.hooks";
import { FormField, FormItem } from "@/components/ui/form";
import { cn } from "@/utils/tailwind/utils";
import { FILTER_LANGUAGE_ALL } from "./SearchPage.hooks";

type FilterViewProps = Pick<
  ReturnType<typeof useSearchPage>,
  "form" | "onSubmit" | "filterModelGroups" | "filterLanguages" | "filterCategories" | "onClickFilterClear"
> & {
  showSubmitButton?: boolean;
  showCloseButton?: boolean;
  onClickCloseButton?: () => void;
  formScrollAreaClassName?: string;
  onClickReferenceButton: () => void;
};

export const FilterView: FC<FilterViewProps> = ({
  form,
  onSubmit,
  filterModelGroups,
  filterLanguages,
  filterCategories,
  onClickFilterClear,
  showSubmitButton = true,
  showCloseButton = false,
  onClickCloseButton,
  formScrollAreaClassName,
  onClickReferenceButton,
}) => {
  const { t } = useTranslation();

  return (
    <form
      className="bg-white py-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (showSubmitButton) {
          onSubmit();
        }
      }}
    >
      <div className="flex flex-row items-center px-4">
        <FilterIcon className="h-4 w-4" />
        <label className="mx-2 text-base">{t("filter_title")}</label>
        <div className="flex-1" />
        <Button variant="secondary" type="button" onClick={onClickFilterClear}>
          <p className="text-sm">{t("filter_clear")}</p>
        </Button>
      </div>

      <ScrollArea className={cn("sm:h-[calc(100svh-222px)]", formScrollAreaClassName)}>
        <div className="flex flex-col gap-y-4 px-4 py-4">
          <FormField
            control={form.control}
            name={"filter.modelGroup"}
            render={({ field }) => (
              <FormItem>
                <label className="text-sm">{t("filter_model_gr")}</label>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                    <ChevronDownIcon className="h-4 w-4" />
                  </SelectTrigger>
                  <SelectContent>
                    {filterModelGroups.map((data, index) => (
                      <SelectItem key={index} value={data.value}>
                        {data.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-y-2">
            <div className="text-sm">{t("filter_model_code")}</div>
            <div className="flex flex-row gap-x-2">
              <Input className="text-sm" {...form.register("filter.modelCode")} />
              <Button
                onClick={onClickReferenceButton}
                className="min-h-10 min-w-10 p-0"
                variant="secondary"
                type="button"
              >
                <SquareArrowOutUpRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <div className="text-sm">{t("filter_model_name")}</div>
            <div className="flex flex-row gap-x-2">
              <Input className="text-sm" {...form.register("filter.modelName")} />
              <Button
                onClick={onClickReferenceButton}
                className="min-h-10 min-w-10 p-0"
                variant="secondary"
                type="button"
              >
                <SquareArrowOutUpRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <FormField
            control={form.control}
            name={"filter.language"}
            render={({ field }) => (
              <FormItem>
                <label className="text-sm">{t("filter_language")}</label>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                    <ChevronDownIcon className="h-4 w-4" />
                  </SelectTrigger>
                  <SelectContent>
                    {filterLanguages.map((data, index) => (
                      <SelectItem key={index} value={data.value}>
                        {data.label === FILTER_LANGUAGE_ALL ? t("filter_language_all") : data.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <p className="text-sm">{t("filter_info_category")}</p>
          {filterCategories.length === 0 && <p className="text-sm">{t("filter_info_category_none")}</p>}
          {filterCategories.map((data, index) => {
            return (
              <FormField
                key={index}
                control={form.control}
                name={"filter.categories"}
                render={({ field }) => {
                  return (
                    <FormItem className="flex items-center gap-x-2 space-y-0">
                      <Checkbox
                        id={`checkbox-${data.value}`}
                        checked={field.value?.includes(data.value)}
                        onCheckedChange={(checked) => {
                          return checked
                            ? field.onChange([...(field.value ?? []), data.value])
                            : field.onChange(field.value?.filter((value) => value !== data.value));
                        }}
                      />
                      <label className="text-sm" htmlFor={`checkbox-${data.value}`}>
                        {data.label}
                      </label>
                    </FormItem>
                  );
                }}
              />
            );
          })}
          <p className="text-sm">{t("filter_scope")}</p>
          <FormField
            control={form.control}
            name={"filter.onlyLatestVersion"}
            render={({ field }) => {
              return (
                <FormItem className="flex items-center gap-x-2 space-y-0">
                  <Checkbox id={`checkbox-onlyLatestVersion`} checked={field.value} onCheckedChange={field.onChange} />
                  <label className="text-sm" htmlFor={`checkbox-onlyLatestVersion`}>
                    {t("filter_scope_latest")}
                  </label>
                </FormItem>
              );
            }}
          />
        </div>
      </ScrollArea>

      <div className="flex flex-col gap-y-2 px-4">
        {showSubmitButton && (
          <Button variant="default" className="w-full">
            <p className="text-sm">{t("apply")}</p>
          </Button>
        )}
        {showCloseButton && (
          <Button variant="secondary" className="w-full" type="button" onClick={onClickCloseButton}>
            <p className="text-sm">{t("close")}</p>
          </Button>
        )}
      </div>
    </form>
  );
};
